import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AdminProjects } from "@/components/admin/AdminProjects";
import { AdminPricing } from "@/components/admin/AdminPricing";
import { AdminTestimonials } from "@/components/admin/AdminTestimonials";
import { AdminMaterials } from "@/components/admin/AdminMaterials";
import { AdminPortfolio } from "@/components/admin/AdminPortfolio";
import { AdminProposals } from "@/components/admin/AdminProposals";
import { AdminFunnel } from "@/components/admin/AdminFunnel";
import { AdminObatExport } from "@/components/admin/AdminObatExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0);

const STATUSES = [
    { v: "nouveau", l: "Nouveau" },
    { v: "contacte", l: "Contacté" },
    { v: "devis_envoye", l: "Devis envoyé" },
    { v: "signe", l: "Signé" },
    { v: "clos", l: "Clos" },
];

const TABS = [
    { k: "quotes", l: "Devis" },
    { k: "appointments", l: "Rendez-vous" },
    { k: "contacts", l: "Messages" },
    { k: "payments", l: "Paiements" },
    { k: "clients", l: "Clients" },
];

const CUSTOM_TABS = [
    { k: "proposals", l: "Devis signables" },
    { k: "projects", l: "Chantiers" },
    { k: "portfolio", l: "Portfolio" },
    { k: "materials", l: "Matériaux" },
    { k: "testimonials", l: "Avis" },
    { k: "pricing", l: "Prix" },
    { k: "obat", l: "Export Obat" },
];

export default function AdminPage() {
    const { user, ready } = useAuth();
    const [overview, setOverview] = useState(null);
    const [tab, setTab] = useState("quotes");
    const [data, setData] = useState({});

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        axios.get(`${API}/admin/overview`).then((r) => setOverview(r.data)).catch(() => {});
        Promise.all(TABS.map((t) =>
            axios.get(`${API}/admin/${t.k}`).then((r) => [t.k, r.data[t.k] || []]).catch(() => [t.k, []])
        )).then((entries) => setData(Object.fromEntries(entries)));
    }, [user]);

    if (!ready) return null;
    if (!user) return <Navigate to="/connexion" replace />;
    if (user.role !== "admin") return <Navigate to="/mon-espace" replace />;

    const setStatus = async (kind, id, status) => {
        try {
            await axios.patch(`${API}/admin/${kind}/${id}/status`, { status });
            setData((d) => ({
                ...d,
                [kind]: d[kind].map((x) => (x.id === id ? { ...x, status } : x)),
            }));
            toast.success("Statut mis à jour");
        } catch {
            toast.error("Erreur de mise à jour");
        }
    };

    const stats = overview ? [
        { label: "Devis", value: overview.quotes },
        { label: "Rendez-vous", value: overview.appointments },
        { label: "Messages", value: overview.contacts },
        { label: "Clients", value: overview.clients },
        { label: "Paiements", value: overview.payments_paid },
        { label: "Revenus IA", value: `${fmt(overview.revenue_eur)} €` },
    ] : [];

    return (
        <div data-testid="admin-page" className="min-h-screen bg-[#FAF9F6]">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="h-px w-10 bg-[#B89C6A]" />
                        <span className="tracking-luxe text-[#B89C6A]">Tableau de bord</span>
                    </div>
                    <h1 className="font-serif-lux text-4xl md:text-5xl mb-12">
                        Pilotage <em className="italic text-[#B89C6A]">CM RENOV</em>.
                    </h1>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
                        {stats.map((s) => (
                            <div key={s.label} data-testid={`admin-stat-${s.label}`} className="lux-card p-5">
                                <div className="tracking-luxe text-[#B89C6A] text-[10px]">{s.label}</div>
                                <div className="font-serif-lux text-3xl mt-2">{s.value}</div>
                            </div>
                        ))}
                    </div>

                    <AdminFunnel />

                    <div className="flex flex-wrap gap-2 mb-8">
                        {TABS.map((t) => (
                            <button
                                key={t.k}
                                data-testid={`admin-tab-${t.k}`}
                                onClick={() => setTab(t.k)}
                                className={`px-4 py-2 border text-[11px] tracking-wider uppercase transition ${tab === t.k ? "border-[#B89C6A] bg-[#B89C6A]/10" : "hairline"}`}
                            >
                                {t.l} ({(data[t.k] || []).length})
                            </button>
                        ))}
                        {CUSTOM_TABS.map((t) => (
                            <button
                                key={t.k}
                                data-testid={`admin-tab-${t.k}`}
                                onClick={() => setTab(t.k)}
                                className={`px-4 py-2 border text-[11px] tracking-wider uppercase transition ${tab === t.k ? "border-[#B89C6A] bg-[#B89C6A]/10" : "hairline"}`}
                            >
                                {t.l}
                            </button>
                        ))}
                    </div>

                    {tab === "proposals" && <AdminProposals />}
                    {tab === "projects" && <AdminProjects />}
                    {tab === "portfolio" && <AdminPortfolio />}
                    {tab === "materials" && <AdminMaterials />}
                    {tab === "testimonials" && <AdminTestimonials />}
                    {tab === "pricing" && <AdminPricing />}
                    {tab === "obat" && <AdminObatExport />}

                    <div data-testid="admin-list" className="space-y-3">
                        {TABS.some((t) => t.k === tab) && (data[tab] || []).length === 0 && (
                            <p className="text-[13px] text-[#1C1E1D]/50 italic">Aucun élément.</p>
                        )}
                        {tab === "quotes" && (data.quotes || []).map((q) => (
                            <Card key={q.id}
                                title={`${(q.input?.project_type || "").replace(/_/g, " ")} · ${q.input?.surface_m2 || "?"} m²`}
                                lines={[
                                    `${q.full_name || "—"} · ${q.email || ""} · ${q.phone || "—"}`,
                                    `Estimation : ${fmt(q.breakdown?.total_min)} — ${fmt(q.breakdown?.total_max)} €`,
                                    q.message ? `« ${q.message.slice(0, 140)} »` : null,
                                ]}
                                date={q.created_at}
                                status={<StatusSelect value={q.status} onChange={(s) => setStatus("quotes", q.id, s)} />}
                            />
                        ))}
                        {tab === "appointments" && (data.appointments || []).map((a) => (
                            <Card key={a.id}
                                title={`${a.date} · ${a.time_slot}`}
                                lines={[
                                    `${a.full_name || "—"} · ${a.email || ""} · ${a.phone || "—"}`,
                                    `${a.project_type || "Rendez-vous"} — ${a.address || "adresse à confirmer"}`,
                                ]}
                                date={a.created_at}
                                status={<StatusSelect value={a.status} onChange={(s) => setStatus("appointments", a.id, s)} />}
                            />
                        ))}
                        {tab === "contacts" && (data.contacts || []).map((c) => (
                            <Card key={c.id}
                                title={c.subject || "Message"}
                                lines={[
                                    `${c.full_name || "—"} · ${c.email || ""}`,
                                    c.message ? `« ${c.message.slice(0, 200)} »` : null,
                                ]}
                                date={c.created_at}
                                status={<StatusSelect value={c.status} onChange={(s) => setStatus("contacts", c.id, s)} />}
                            />
                        ))}
                        {tab === "payments" && (data.payments || []).map((p) => (
                            <Card key={p.session_id}
                                title={`${fmt(p.amount_eur)} € · ${p.credits} crédit(s)`}
                                lines={[p.email || "—"]}
                                date={p.created_at}
                                status={
                                    <span className={`text-[11px] tracking-wider uppercase px-3 py-1 border ${p.payment_status === "paid" ? "border-green-600 text-green-700" : "hairline text-[#1C1E1D]/50"}`}>
                                        {p.payment_status === "paid" ? "Payé" : "En attente"}
                                    </span>
                                }
                            />
                        ))}
                        {tab === "clients" && (data.clients || []).map((u) => (
                            <Card key={u.id}
                                title={u.full_name || u.email}
                                lines={[u.email, `Crédits rendus IA : ${u.render_credits ?? 0}`]}
                                date={u.created_at}
                            />
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function StatusSelect({ value, onChange }) {
    return (
        <select
            data-testid="admin-status-select"
            className="lux-input text-[11px] py-1.5"
            value={value || "nouveau"}
            onChange={(e) => onChange(e.target.value)}
        >
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
    );
}

function Card({ title, lines, date, status }) {
    return (
        <div className="lux-card p-5 flex justify-between items-start flex-wrap gap-3">
            <div className="min-w-0">
                <div className="font-serif-lux text-lg capitalize">{title}</div>
                {(lines || []).filter(Boolean).map((l, i) => (
                    <div key={i} className="text-[13px] text-[#1C1E1D]/70 mt-1">{l}</div>
                ))}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
                <span className="text-[11px] tracking-luxe text-[#B89C6A]">{date?.slice(0, 10)}</span>
                {status}
            </div>
        </div>
    );
}
